/**
 * Helpers canônicos de documento (CPF / CNPJ / CAEPF).
 *
 * CNPJ segue o formato alfanumérico da Receita Federal (vigente desde jul/2026):
 * 14 caracteres, os 12 primeiros `[A-Z0-9]` e os 2 últimos dígitos verificadores
 * numéricos. O CNPJ numérico legado é subset. CPF e CAEPF continuam numéricos.
 *
 * Regra de ouro: NUNCA `replace(/\D/g, '')`, `Number()`, `parseInt` ou
 * `padStart` em documento — apaga letras e fabrica outro documento. Use
 * `cleanCnpjOrCpf` como único normalizador.
 */

/**
 * Remove separadores de máscara (`.`, `/`, `-`, espaços) e devolve a string em
 * caracteres canônicos (`A-Z` + `0-9`, maiúsculas). Para CPF e CAEPF
 * (numéricos) o resultado é idêntico ao `replace(/\D/g, '')`, então pode
 * substituir esse padrão sem regressão.
 */
export function cleanCnpjOrCpf(value: string | null | undefined): string {
  return (value ?? '').replace(/[^A-Z0-9]/gi, '').toUpperCase();
}

/** CPF normalizado (11 dígitos) → `XXX.XXX.XXX-XX`. Tamanho diferente volta como veio. */
export function formatCpf(value: string | null | undefined): string {
  const doc = cleanCnpjOrCpf(value);
  if (!/^\d{11}$/.test(doc)) return value ?? '';
  return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * CNPJ normalizado (14 chars, numérico ou alfanumérico) → `XX.XXX.XXX/XXXX-XX`.
 * Tamanho diferente volta como veio.
 */
export function formatCnpj(value: string | null | undefined): string {
  const doc = cleanCnpjOrCpf(value);
  if (doc.length !== 14) return value ?? '';
  return doc.replace(
    /([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})([A-Z0-9]{2})/,
    '$1.$2.$3/$4-$5'
  );
}

/** CAEPF normalizado (14 dígitos) → `XXX.XXX.XXX/XXX-XX`. Tamanho diferente volta como veio. */
export function formatCaepf(value: string | null | undefined): string {
  const doc = cleanCnpjOrCpf(value);
  if (!/^\d{14}$/.test(doc)) return value ?? '';
  return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3/$4-$5');
}
